import { fetchOfferListFilters } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { useEffect, useMemo } from 'react'

function mergeFilterItemsToSet(set: Set<string>, items: Array<App.FilterItem>) {
  for (const item of items) {
    set.add(item.slug)
  }
}

function mergeValueCountArrayToSet(set: Set<string>, valueCounts: Array<{ value: string, count: number }>) {
  for (const valueCount of valueCounts) {
    set.add(valueCount.value)
  }
}

function mergeRecordToSet(set: Set<string>, record: Record<string, number>) {
  for (const key of Object.keys(record)) {
    set.add(key)
  }
}

export function useMultipleOfferListFilters(filtersList: Array<App.OfferListFilters>): App.OfferListFilterOptions {
  const dispatch = useAppDispatch()
  useEffect(() => {
    for (const filters of filtersList) {
      dispatch(fetchOfferListFilters(filters))
    }
  }, [filtersList, dispatch])
  const offerListFilterOption = useAppSelector(state => state.offer.offerListFilterOptions)
  const listFilters = useMemo(() => {
    const combinedFilters: App.OfferListFilterOptions = {
      error: undefined,
      fetching: false,
      filterOrder: {
        amenities: {},
        holidayTypes: {},
        propertyTypes: {},
      },
      filters: {
        amenities: [],
        propertyFacilities: [],
        roomAmenities: [],
        bedTypes: [],
        bedrooms: [],
        campaigns: [],
        holidayTypes: [],
        offerTypes: [],
        propertyTypes: [],
        luxPlusFeatures: [],
        loyalty: [],
        agentHubFeatures: [],
        paymentOptions: [],
        chains: [],
        inclusions: [],
        customerRatings: [],
        total: 0,
      },
      orderedFilters: {
        amenities: [],
        holidayTypes: [],
      },
    }

    const filterSlugSets: Record<string, Set<string>> = {
      holidayTypes: new Set(),
      propertyTypes: new Set(),
      campaigns: new Set(),
      amenities: new Set(),
      propertyFacilities: new Set(),
      roomAmenities: new Set(),
      bedTypes: new Set(),
      bedrooms: new Set(),
      customerRatings: new Set(),
      luxPlusFeatures: new Set(),
      loyalty: new Set(),
      agentHubFeatures: new Set(),
      inclusions: new Set(),
      paymentOptions: new Set(),
      chains: new Set(),
      offerTypes: new Set(),
    }
    const orderedFiltersCategorySet = new Set<string>()
    const orderedFiltersHolidayTypesCategorySet = new Set<string>()
    const filtersOrderAmenitiesCategorySet = new Set<string>()
    const filtersOrderHolidayTypesOrderSet = new Set<string>()
    let hasTotal = false

    filtersList.forEach(filter => {
      const key = getOfferListKey(filter)
      const filterOptions = offerListFilterOption[key]
      if (!filterOptions) return

      if (filterOptions?.filters) {
        mergeFilterItemsToSet(filterSlugSets.holidayTypes, filterOptions.filters.holidayTypes)
        mergeFilterItemsToSet(filterSlugSets.propertyTypes, filterOptions.filters.propertyTypes)
        mergeFilterItemsToSet(filterSlugSets.campaigns, filterOptions.filters.campaigns)
        mergeFilterItemsToSet(filterSlugSets.amenities, filterOptions.filters.amenities)
        mergeFilterItemsToSet(filterSlugSets.propertyFacilities, filterOptions.filters.propertyFacilities)
        mergeFilterItemsToSet(filterSlugSets.roomAmenities, filterOptions.filters.roomAmenities)
        mergeFilterItemsToSet(filterSlugSets.bedTypes, filterOptions.filters.bedTypes)
        mergeFilterItemsToSet(filterSlugSets.bedrooms, filterOptions.filters.bedrooms)
        mergeFilterItemsToSet(filterSlugSets.customerRatings, filterOptions.filters.customerRatings)
        mergeFilterItemsToSet(filterSlugSets.luxPlusFeatures, filterOptions.filters.luxPlusFeatures)
        mergeFilterItemsToSet(filterSlugSets.loyalty, filterOptions.filters.loyalty)
        mergeFilterItemsToSet(filterSlugSets.agentHubFeatures, filterOptions.filters.agentHubFeatures)
        mergeFilterItemsToSet(filterSlugSets.inclusions, filterOptions.filters.inclusions)
        mergeFilterItemsToSet(filterSlugSets.paymentOptions, filterOptions.filters.paymentOptions)
        mergeFilterItemsToSet(filterSlugSets.chains, filterOptions.filters.chains)
        mergeFilterItemsToSet(filterSlugSets.offerTypes, filterOptions.filters.offerTypes)
        hasTotal = true
      }

      if (filterOptions.orderedFilters && combinedFilters.orderedFilters) {
        if (filterOptions.orderedFilters.amenities) {
          mergeValueCountArrayToSet(orderedFiltersCategorySet, filterOptions.orderedFilters.amenities)
        }
        if (filterOptions.orderedFilters.holidayTypes) {
          mergeValueCountArrayToSet(orderedFiltersHolidayTypesCategorySet, filterOptions.orderedFilters.holidayTypes)
        }
      }

      if (filterOptions.filterOrder && combinedFilters.filterOrder) {
        if (filterOptions.filterOrder?.amenities) {
          mergeRecordToSet(filtersOrderAmenitiesCategorySet, filterOptions.filterOrder.amenities)
        }
        if (filterOptions.filterOrder?.holidayTypes) {
          mergeRecordToSet(filtersOrderHolidayTypesOrderSet, filterOptions.filterOrder.holidayTypes)
        }
      }
    })

    if (combinedFilters.filters) {
      const populateFromSet = (set: Set<string>): Array<App.FilterItem> =>
        Array.from(set).map(slug => ({ slug, label: slug, count: 0 }))

      combinedFilters.filters.holidayTypes = populateFromSet(filterSlugSets.holidayTypes)
      combinedFilters.filters.propertyTypes = populateFromSet(filterSlugSets.propertyTypes)
      combinedFilters.filters.campaigns = populateFromSet(filterSlugSets.campaigns)
      combinedFilters.filters.amenities = populateFromSet(filterSlugSets.amenities)
      combinedFilters.filters.propertyFacilities = populateFromSet(filterSlugSets.propertyFacilities)
      combinedFilters.filters.roomAmenities = populateFromSet(filterSlugSets.roomAmenities)
      combinedFilters.filters.bedTypes = populateFromSet(filterSlugSets.bedTypes)
      combinedFilters.filters.bedrooms = populateFromSet(filterSlugSets.bedrooms)
      combinedFilters.filters.customerRatings = populateFromSet(filterSlugSets.customerRatings)
      combinedFilters.filters.luxPlusFeatures = populateFromSet(filterSlugSets.luxPlusFeatures)
      combinedFilters.filters.loyalty = populateFromSet(filterSlugSets.loyalty)
      combinedFilters.filters.agentHubFeatures = populateFromSet(filterSlugSets.agentHubFeatures)
      combinedFilters.filters.inclusions = populateFromSet(filterSlugSets.inclusions)
      combinedFilters.filters.paymentOptions = populateFromSet(filterSlugSets.paymentOptions)
      combinedFilters.filters.chains = populateFromSet(filterSlugSets.chains)
      combinedFilters.filters.offerTypes = populateFromSet(filterSlugSets.offerTypes)
      if (hasTotal) {
        combinedFilters.filters.total = 0
      }
    }

    if (orderedFiltersCategorySet.size > 0 && combinedFilters.orderedFilters) {
      combinedFilters.orderedFilters.amenities = Array.from(orderedFiltersCategorySet.values().map((category) => ({ value: category, label: category, count: 0 })))
    }

    if (orderedFiltersHolidayTypesCategorySet.size > 0 && combinedFilters.orderedFilters) {
      combinedFilters.orderedFilters.holidayTypes = Array.from((orderedFiltersHolidayTypesCategorySet.values()).map((category) => ({ value: category, label: category, count: 0 })))
    }

    if (filtersOrderAmenitiesCategorySet.size > 0 && combinedFilters.filterOrder) {
      combinedFilters.filterOrder.amenities = Object.fromEntries(filtersOrderAmenitiesCategorySet.values().map(category => [category, 0]))
    }
    if (filtersOrderHolidayTypesOrderSet.size > 0 && combinedFilters.filterOrder) {
      combinedFilters.filterOrder.holidayTypes = Object.fromEntries(filtersOrderHolidayTypesOrderSet.values().map(category => [category, 0]))
    }

    return combinedFilters
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersList])

  return listFilters
}
